import type {
  FileRouteContext,
  FileRouteSortBy,
  FileRouteViewMode,
} from '@jotta/types/schemas/FileRouteSchema'
import type { LinkProps, NavLinkProps } from 'react-router-dom'
import {
  EncodedRouteLink,
  EncodedRouteNavLink,
} from '@jotta/ui/EncodedRouterLink'
import { apiPathToRoute } from '../FileRoute/utils/apiPathToRoute'
import { useFileRouteParams } from '../FileRoute/utils/getFileRouteParams'

export interface FileApiPathLinkOwnProps {
  path: string
  context?: FileRouteContext
  viewMode?: FileRouteViewMode
  sortBy?: FileRouteSortBy
  query?: string
}
export type FileApiPathLinkProps<
  T extends NavLinkProps | LinkProps = LinkProps,
> = FileApiPathLinkOwnProps & Omit<T, 'to'>

/**
 * Link to an api path from backend, preserves the current route context
 * (context, viewmode and sortby) unless you specifically override with your own values
 * Extends the React Router Link component, and all of its props are valid except "to"
 * @param props
 * @returns
 */
export function FileApiPathLink(props: FileApiPathLinkProps) {
  const { route, linkProps } = useFileApiPathLinkProps('FileApiPathLink', props)
  return <EncodedRouteLink to={route} {...linkProps} />
}

/**
 * Link to an api path from backend, preserves the current route context
 * (context, viewmode and sortby) unless you specifically override with your own values
 * Extends the React Router NavLink component, and all of its props are valid except
 * "to"
 * @param props
 * @returns
 */
export function FileApiPathNavLink(props: FileApiPathLinkProps<NavLinkProps>) {
  const { route, linkProps } = useFileApiPathLinkProps<NavLinkProps>(
    'FileApiPathLink',
    props,
  )
  return <EncodedRouteNavLink to={route} {...linkProps} />
}

function useFileApiPathLinkProps<
  T extends NavLinkProps | LinkProps = LinkProps,
>(
  name: string,
  {
    path,
    context: contextProp,
    viewMode: viewModeProp,
    sortBy: sortByProp,
    query: queryProp,
    ...props
  }: FileApiPathLinkProps<T>,
) {
  // const debug = Debug(`jotta:files:${name}`)
  const params = useFileRouteParams()
  const context = contextProp || params.context
  const sortBy = sortByProp || params.sortBy
  const viewMode = viewModeProp || params.viewMode
  const query = queryProp || params.query

  const route = apiPathToRoute(path, {
    context,
    sortBy,
    viewMode,
    query,
  })
  return {
    route,
    linkProps: props,
  }
}
